import { useState, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { GoogleReCaptchaProvider, GoogleReCaptcha } from "react-google-recaptcha-v3";
import { useNavigate, useLocation } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { Loader, TextInput } from "../shared";
import { ERROR_CODE_LIST, RegexPattern } from "../../utils";
import { samlLogout } from "../../utils/service";
import { setCookie, getCookie, deleteCookie } from "../../utils/cookies";
import { toast } from "react-toastify";
import { setToken } from "../../reducers/tokens";

const SignIn = () => {
    const {
        register,
        formState: { errors },
        handleSubmit,
    } = useForm({});
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const jwtToken = useSelector((state) => state.tokens.token);
    const location = useLocation();

    const [recaptchaToken, setRecaptchaToken] = useState(null);
    const [loading, setLoading] = useState(false);
    const [relogin, setRelogin] = useState(false);
    const [errMessage, setErrMessage] = useState(null);

    const onVerify = useCallback((recaptchaToken) => {
        setRecaptchaToken(recaptchaToken);
    }, []);

    useEffect(() => {
        const jwtToken = getCookie("piap_jwt_token");
        const relogin = getCookie("relogin");
        const errMessage = getCookie("errMessage");

        if (jwtToken) {
            navigate("/");
            return;
        } else {
            location.pathname !== "/sign-in" && samlLogout();
        }

        if (relogin) {
            setRelogin(true);
            deleteCookie("relogin");
        }

        if (errMessage) {
            deleteCookie("errMessage");
            setErrMessage("Your Account is Inactive. Please contact with administrator");
        }
    }, [jwtToken]);

    const submitAction = handleSubmit(async (data) => {
        const { email, password } = data;

        if (recaptchaToken) {
            setLoading(true);
            await loginCallback(email, password);
            setLoading(false);
        } else {
            toast.error("Recaptcha not matched");
        }
    });

    const loginCallback = async (email, password) => {
        const loginUrl = `${process.env.REACT_APP_BASE_URL}/login`;
        try {
            const response = await fetch(loginUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    email,
                    password,
                    recaptchaToken,
                }),
            });

            const result = await response.json();
            if (!result?.errors?.length > 0) {
                const jwt = result.data.adminAuthenticate.jwt;
                const hostUrl = result.hostUrl;
                if (jwt !== null) {
                    setCookie("piap_jwt_token", jwt);
                    setCookie("piap_host_url", hostUrl);
                    dispatch(setToken(jwt));
                    navigate("/");
                } else {
                    toast.error("Invalid Email or Password!");
                }
            } else {
                const errMessage = ERROR_CODE_LIST[result.errors[0].errcode] || "Invalid Request";
                toast.error(errMessage);
                window.location.reload();
            }
        } catch (error) {
            toast.error(error.message);
            setTimeout(() => {
                window.location.reload();
            }, 1000);
        }
    };

    const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
    const isCustomLogin = process.env.REACT_APP_CUSTOM_LOGIN === "true";

    return (
        <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey}>
            <GoogleReCaptcha onVerify={onVerify} />

            <div className="row justify-content-center">
                <div className="col-lg-6 col-md-8">
                    {loading && <Loader />}
                    <div className="p-5 custom-shadow">
                        {errMessage && (
                            <>
                                <div className="alert alert-danger" role="alert">
                                    <small> {errMessage}</small>
                                </div>

                                <a href={`/saml/login`} className="btn btn-secondary btn-block">
                                    Sign In With <strong>511</strong>
                                </a>
                            </>
                        )}

                        {relogin && !errMessage && (
                            <>
                                <a href={`/saml/login`} className="btn btn-secondary btn-block">
                                    Sign In With <strong>511</strong>
                                </a>
                            </>
                        )}

                        {isCustomLogin && (
                            <form noValidate onSubmit={submitAction}>
                                <h2 className="fw-bold text-center mb-5">Login</h2>

                                <TextInput
                                    id="email"
                                    labelText="Email"
                                    type="email"
                                    {...register("email", {
                                        pattern: RegexPattern.email,
                                        required: true,
                                    })}
                                    required
                                    isInvalid={!!errors.email}
                                />
                                <TextInput
                                    id="password"
                                    labelText="Password"
                                    type="password"
                                    {...register("password", {
                                        required: true,
                                    })}
                                    required
                                    isInvalid={!!errors.password}
                                />

                                <button
                                    type="submit"
                                    className="w-100 btn btn-primary btn-lg rounded-1"
                                >
                                    Login
                                </button>
                                <div className="text-center my-3">
                                    <b>OR</b>
                                </div>
                                <a
                                    href="/saml/login"
                                    className="btn btn-secondary w-100 btn-lg rounded-1"
                                >
                                    Sign In With <strong>511</strong>
                                </a>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </GoogleReCaptchaProvider>
    );
};

export default SignIn;
